import React from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Checkbox } from '@/components/ui/checkbox'
import { Slider } from '@/components/ui/slider'
import { Separator } from '@/components/ui/separator'
import {
  DISPLAY_TYPES,
  EXPERIENCE_TYPES,
  HOLIDAY_THEMES,
  ENTERTAINMENT_FEATURES,
  ACCESSIBILITY_FEATURES,
  TECHNICAL_FEATURES,
  FiltersState,
  defaultFiltersState,
} from '@/lib/filter-options'
import { MapPin } from 'lucide-react'

type Props = {
  value: FiltersState
  onChange: (next: FiltersState) => void
  onClearAll?: () => void
  onUseCurrentLocation?: () => void
  locationEnabled?: boolean
}

function toggleInList<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((i) => i !== item) : [...list, item]
}

export const DisplayFiltersSidebar: React.FC<Props> = ({
  value,
  onChange,
  onClearAll,
  onUseCurrentLocation,
  locationEnabled,
}) => {
  const set = (partial: Partial<FiltersState>) => onChange({ ...value, ...partial })

  return (
    <aside className="w-full md:w-80 shrink-0 border-r bg-background">
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Filters</h2>
          <button
            className="text-sm text-muted-foreground hover:underline"
            onClick={() => (onClearAll ? onClearAll() : set(defaultFiltersState))}
          >
            Clear all
          </button>
        </div>

        {/* Search */}
        <Accordion type="single" collapsible defaultValue="search">
          <AccordionItem value="search">
            <AccordionTrigger className="text-base">Search</AccordionTrigger>
            <AccordionContent>
              <Input
                placeholder="Search displays"
                value={value.query}
                onChange={(e) => set({ query: e.target.value })}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Separator />

        <Accordion type="multiple" defaultValue={["type","experience","holiday","features","schedule","community","location"]}>
          {/* Display Type */}
          <AccordionItem value="type">
            <AccordionTrigger className="text-base">Display Type</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {DISPLAY_TYPES.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={value.types.includes(t)}
                      onCheckedChange={() => set({ types: toggleInList(value.types, t) })}
                    />
                    <span>{t}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Experience */}
          <AccordionItem value="experience">
            <AccordionTrigger className="text-base">Experience</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {EXPERIENCE_TYPES.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={value.experiences.includes(t)}
                      onCheckedChange={() => set({ experiences: toggleInList(value.experiences, t) })}
                    />
                    <span>{t}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Holiday / Theme */}
          <AccordionItem value="holiday">
            <AccordionTrigger className="text-base">Holiday / Theme</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {HOLIDAY_THEMES.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={value.holidays.includes(t)}
                      onCheckedChange={() => set({ holidays: toggleInList(value.holidays, t as any) })}
                    />
                    <span>{t}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Features & Amenities */}
          <AccordionItem value="features">
            <AccordionTrigger className="text-base">Features & Amenities</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-2">Entertainment</p>
                  <div className="space-y-2">
                    {ENTERTAINMENT_FEATURES.map((f) => (
                      <label key={f} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={value.entertainment.includes(f)}
                          onCheckedChange={() => set({ entertainment: toggleInList(value.entertainment, f) })}
                        />
                        <span>{f}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">Accessibility</p>
                  <div className="space-y-2">
                    {ACCESSIBILITY_FEATURES.map((f) => (
                      <label key={f} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={value.accessibility.includes(f)}
                          onCheckedChange={() => set({ accessibility: toggleInList(value.accessibility, f) })}
                        />
                        <span>{f}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">Technical</p>
                  <div className="space-y-2">
                    {TECHNICAL_FEATURES.map((f) => (
                      <label key={f} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={value.technical.includes(f)}
                          onCheckedChange={() => set({ technical: toggleInList(value.technical, f) })}
                        />
                        <span>{f}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Schedule & Availability */}
          <AccordionItem value="schedule">
            <AccordionTrigger className="text-base">Schedule & Availability</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Start date</label>
                    <Input type="date" value={value.dateFrom ?? ''} onChange={(e) => set({ dateFrom: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">End date</label>
                    <Input type="date" value={value.dateTo ?? ''} onChange={(e) => set({ dateTo: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Opens before</label>
                    <Input type="time" value={value.opensBefore ?? ''} onChange={(e) => set({ opensBefore: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Closes after</label>
                    <Input type="time" value={value.closesAfter ?? ''} onChange={(e) => set({ closesAfter: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    { key: 'openWeekdays', label: 'Open Weekdays' },
                    { key: 'openWeekends', label: 'Open Weekends' },
                    { key: 'openHolidays', label: 'Open Holidays' },
                    { key: 'currentlyActive', label: 'Currently Active' },
                  ].map((opt) => (
                    <label key={opt.key} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={(value as any)[opt.key] as boolean}
                        onCheckedChange={() => set({ [opt.key]: !(value as any)[opt.key] } as any)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Community & Social */}
          <AccordionItem value="community">
            <AccordionTrigger className="text-base">Community & Social</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={value.hasReviews} onCheckedChange={() => set({ hasReviews: !value.hasReviews })} />
                  <span>Has Reviews</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm w-24">Min rating</span>
                  <Slider
                    value={[value.minRating]}
                    min={0}
                    max={5}
                    step={0.5}
                    onValueChange={(v) => set({ minRating: v[0] })}
                  />
                  <span className="text-xs w-8 text-right">{value.minRating}</span>
                </div>
                {[
                  { key: 'verified', label: 'Verified Display' },
                  { key: 'awards', label: 'Awards/Recognition' },
                  { key: 'charity', label: 'Charity/Donation Supported' },
                ].map((opt) => (
                  <label key={opt.key} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={(value as any)[opt.key] as boolean}
                      onCheckedChange={() => set({ [opt.key]: !(value as any)[opt.key] } as any)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Location */}
          <AccordionItem value="location">
            <AccordionTrigger className="text-base">Location</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3">
                <Button type="button" variant="secondary" className="w-full justify-start" onClick={onUseCurrentLocation}>
                  <MapPin className="h-4 w-4 mr-2" /> Use current location
                </Button>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Within (miles)</span>
                    <span className="text-xs text-muted-foreground">{value.radiusMiles} miles</span>
                  </div>
                  <Slider value={[value.radiusMiles]} min={5} max={100} step={5} onValueChange={(v) => set({ radiusMiles: v[0] })} />
                  <p className="text-xs text-muted-foreground">
                    Tip: If location is not set, distance filter is ignored.
                  </p>
                  {locationEnabled === false && (
                    <p className="text-xs text-muted-foreground">Location permission not granted.</p>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </aside>
  )
}

export default DisplayFiltersSidebar

